import styles from "./Navbar.module.scss";
import { useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavContext from "../../context/NavContext";
import { NavLink } from "react-router-dom";
import {
  MdQueryStats,
  MdOutlineAdminPanelSettings,
  MdMenu,
  MdClose,
  MdOutlineSettings,
  MdOutlineEmail,
} from "react-icons/md";
import { FcStatistics } from "react-icons/fc";
import { VscNotebookTemplate } from "react-icons/vsc";
import { AiOutlineTable } from "react-icons/ai";
import { FaMicrophoneAlt, FaFileAudio } from "react-icons/fa";
import { GiBoneKnife } from "react-icons/gi";
import { FiFileText } from "react-icons/fi";
import logo from "../../pics/logo-hearingspace.png";
import { GrLicense, GrCertificate, GrUserManager } from "react-icons/gr";
import MyProfile from "../RightNavbar/Submenus/MyProfile";
import InfosConnexion from "../superAdmin/infos/InfosConnexion";
import Axios from "../../_services/caller.service";
import { useIsTabletDown } from "../../hooks/useBreakpoint";

const TabLink = ({ url, icon, label, end, onClick }) => (
  <NavLink
    to={url}
    end={end}
    onClick={onClick}
    className={({ isActive }) =>
      `${styles.tabBtn} ${isActive ? styles.tabActive : ""}`
    }
  >
    {icon}
    <span>{label}</span>
  </NavLink>
);

const Navbar = () => {
  const { superAdmin, supportTeam, ownerId, setOwnerId } = useContext(NavContext);
  const { t } = useTranslation();
  const location = useLocation();
  const isTabletDown = useIsTabletDown();
  const isPrivilegedUser = Boolean(superAdmin) || Boolean(supportTeam);

  const [infosUser, setInfosUser] = useState([]);
  const [cabineAccess, setCabineAccess] = useState(true);
  const [ownerLink, setOwnerLink] = useState("");
  const [userIsOwner, setUserIsOwner] = useState(false);
  const [inviteOwnerId, setInviteOwnerId] = useState("");
  // Burger menu (tablette/mobile uniquement)
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!superAdmin && !supportTeam) return;
    Axios.get(`/relationUser/${ownerId}`)
      .then((response) => {
        const data = response.data.data;
        setInfosUser(data.user);
        setOwnerLink(data.owner);
        setCabineAccess(data.userType !== "Aucun accès à une cabine");
      })
      .catch(() => {});
  }, [ownerId, superAdmin, supportTeam]);

  // Ferme le menu burger à chaque navigation
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock scroll du body quand burger ouvert
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  // Ferme avec ESC
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const isPrivileged = cabineAccess && isPrivilegedUser;
  const closeMenu = () => setMenuOpen(false);

  const tabsContent = (
    <>
      <TabLink onClick={closeMenu} url="/private/superAdmin" icon={<MdOutlineAdminPanelSettings />} label="Cabines" end />
      <TabLink onClick={closeMenu} url="/private/superAdmin/manageSubscriptions" icon={<AiOutlineTable />} label="Subscriptions" />
      <TabLink onClick={closeMenu} url="/private/superAdmin/manageUserPreset" icon={<VscNotebookTemplate />} label="Preset" />
      <TabLink onClick={closeMenu} url="/private/superAdmin/manageMics" icon={<FaMicrophoneAlt />} label="Mics" />
      <TabLink onClick={closeMenu} url="/private/superAdmin/manageCO" icon={<GiBoneKnife />} label="CO" />
      <TabLink onClick={closeMenu} url="/private/superAdmin/diracLicence" icon={<GrLicense />} label="Dirac" />
      <TabLink onClick={closeMenu} url="/private/superAdmin/wordsLicence" icon={<FaFileAudio />} label="Words" />
      <TabLink onClick={closeMenu} url="/private/superAdmin/manageCertificat" icon={<GrCertificate />} label="Certificat" />
      <TabLink onClick={closeMenu} url="/private/superAdmin/demandes" icon={<FiFileText />} label={t("admin_requests.tab")} />
      {superAdmin && (
        <TabLink onClick={closeMenu} url="/private/superAdmin/managePermissions" icon={<GrUserManager />} label="Permissions" />
      )}
      <TabLink onClick={closeMenu} url="/private/superAdmin/statistics" icon={<FcStatistics />} label="Statistics" />
      <TabLink onClick={closeMenu} url="/private/dashboard" icon={<MdQueryStats />} label="Dashboard" />
      {superAdmin && (
        <TabLink onClick={closeMenu} url="/private/superAdmin/hsSettings" icon={<MdOutlineSettings />} label="HS Settings" />
      )}
      {superAdmin && (
        <TabLink onClick={closeMenu} url="/private/superAdmin/bulkEmail" icon={<MdOutlineEmail />} label={t("bulk_email.tab")} />
      )}
    </>
  );

  return (
    <nav className={styles.navBar}>
      {!isPrivileged && (
        <div className={styles.noAccess}>
          <img src={logo} alt="logo" className={styles.logo} />
        </div>
      )}

      {/* Burger button — tablette/mobile uniquement */}
      {isPrivilegedUser && isTabletDown && (
        <button
          type="button"
          className={styles.burgerBtn}
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? t("common.close") : t("common.menu", "Menu")}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <MdClose /> : <MdMenu />}
        </button>
      )}

      {/* LEFT — Tabs (desktop) */}
      {isPrivilegedUser && !isTabletDown && (
        <div className={styles.tabBar}>{tabsContent}</div>
      )}

      {/* Mobile drawer */}
      {isPrivilegedUser && isTabletDown && menuOpen && (
        <>
          <div
            className={styles.mobileOverlay}
            onClick={closeMenu}
            aria-hidden="true"
          />
          <div className={styles.mobileMenu} role="dialog" aria-modal="true">
            <div className={styles.mobileMenuHeader}>
              <img src={logo} alt="logo" className={styles.mobileLogo} />
              <button
                type="button"
                className={styles.mobileCloseBtn}
                onClick={closeMenu}
                aria-label={t("common.close")}
              >
                <MdClose />
              </button>
            </div>
            <div className={styles.mobileTabs}>{tabsContent}</div>
          </div>
        </>
      )}

      {/* RIGHT — Infos + Env + Profile */}
      <div className={styles.rightSection}>
        {isPrivileged && (
          <InfosConnexion
            infosUser={infosUser}
            setOwnerId={setOwnerId}
            userIsOwner={userIsOwner}
            inviteOwnerId={inviteOwnerId}
            ownerLink={ownerLink}
            ownerId={ownerId}
          />
        )}
        {process.env.REACT_APP_API_URL.includes("localhost") && (
          <span className={styles.envBadge}>LOCAL</span>
        )}
        {process.env.REACT_APP_API_URL.includes("beta") && (
          <span className={`${styles.envBadge} ${styles.envBeta}`}>BETA</span>
        )}
        <MyProfile />
      </div>
    </nav>
  );
};

export default Navbar;
